import React, { useState, useMemo } from 'react';
import { Table, Button, Form, Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import MoveRequestModal from './MoveRequestModal/MoveRequestModal';
import EditJobModal from './EditJobModal';
// Import centralized date utilities
import { formatDateCompact, formatDateForForm } from '../../utils/dateUtils';
import { getEffectiveRole } from './utils/schedulePermissions';
import { getMoveRequestIcon, getMoveRequestTooltipText } from './utils/jobHelpers';
import SortableTableHeader from "./SortableTableHeader";
import { getNextSortConfig, sortTableRows } from "./utils/tableSorting";
import CompactScheduleSection from "./components/CompactScheduleSection";

const getMoveStatus = (job) => {
  if (job.MoveRequestDateMovedUpOut) return "Moved";
  if (job.ActualShipDate) return "Shipped";
  return job.MoveRequestDateInitiated ? "Pending" : "";
};

const TABLE_COLUMNS = [
  { key: "JobNumber", label: "Job Number", type: "text" },
  { key: "ModelNumber", label: "Model Number", type: "text" },
  { key: "ProjectName", label: "Project Name", type: "text" },
  { key: "PackDue", label: "Pack Due", type: "date" },
  { key: "TransportType", label: "Transport Type", type: "text" },
  { key: "OriginalScheduledShipDate", label: "Original Scheduled Ship Date", type: "date" },
  { key: "CurrentPlanShipDate", label: "Current Plan Ship Date", type: "date" },
  { key: "SalesRep", label: "Sales Rep", type: "text" },
  { key: "JobLocation", label: "Job Location", type: "text" },
  { key: "MoveRequestRequestedDate", label: "Requested Date", type: "date" },
  { key: "MoveRequestReasonForMove", label: "Reason for Move", type: "text" },
  { key: "MoveStatus", label: "Move Status", type: "text", getValue: getMoveStatus },
  { key: "Action", label: "Action", sortable: false },
];

// Helper function for text processing and date formatting - use centralized utility
const formatDateToMMDD = (value) => formatDateCompact(value);

// Strip time from date values - use centralized utility
const stripTimeFromDates = (job) => {
  const dateFields = [
    "PackDue",
    "OriginalScheduledShipDate",
    "CurrentPlanShipDate",
    "MoveRequestPrevDate",
    "MoveRequestDateInitiated",
    "MoveRequestRequestedDate",
    "MoveRequestDateMovedUpOut"
  ];

  const formattedJob = { ...job };

  dateFields.forEach((field) => {
    if (formattedJob[field]) {
      formattedJob[field] = formatDateForForm(formattedJob[field]);
    }
  });

  return formattedJob;
};

const MoveRequests = ({ filteredJobs = null, canEdit = true }) => {
  // Always call hooks unconditionally
  const jobScheduleDataFromRedux = useSelector((state) => state.jobSchedule.data);
  // Use filteredJobs if provided, otherwise use Redux data
  const jobScheduleData = filteredJobs || jobScheduleDataFromRedux;
  // Get user roles to check permissions
  const userRoles = useSelector((state) => state.user?.userRoles) || [];
  // Only allow editing for Admin or Scheduling roles, and only when policy allows it
  const effectiveRole = getEffectiveRole(userRoles);
  const hasEditAccess = Boolean(canEdit) && (effectiveRole === 'Admin' || effectiveRole === 'Scheduling');
  
  const [showModal, setShowModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  // Add filter state
  const [filters, setFilters] = useState({
    showMovedJobs: true,
    showUnfulfilledMoveRequests: true,
    showShippedJobs: false // Hide shipped jobs by default
  });
  // Add state for edit job modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCompactFilters, setShowCompactFilters] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });

  // Helper function to determine if a job is an unfulfilled move request
  const isUnfulfilledMoveRequest = (job) => {
    // Must have ActualShipDate but no official move
    if (!job.ActualShipDate || job.MoveRequestDateMovedUpOut) {
      return false;
    }
    
    // If no planned ship date, consider it unfulfilled
    if (!job.CurrentPlanShipDate) {
      return true;
    }
    
    // Check if shipped before planned date (shipped early without official move)
    const actualDate = new Date(job.ActualShipDate);
    const plannedDate = new Date(job.CurrentPlanShipDate);
    return actualDate < plannedDate;
  };

  // Filter jobs that have MoveRequestDateInitiated set
  const moveRequestJobs = useMemo(() => {
    if (!jobScheduleData) return [];
    
    // Filter jobs with move requests and clean up date fields
    let moveRequests = jobScheduleData
      .filter(job => job.MoveRequestDateInitiated)
      .map(job => stripTimeFromDates(job));
    
    // Apply filters
    if (!filters.showMovedJobs) {
      // Filter out jobs that have been moved (have MoveRequestDateMovedUpOut)
      moveRequests = moveRequests.filter(job => !job.MoveRequestDateMovedUpOut);
    }
    
    if (!filters.showUnfulfilledMoveRequests) {
      // Filter out unfulfilled move requests 
      moveRequests = moveRequests.filter(job => !isUnfulfilledMoveRequest(job));
    }
    
    if (!filters.showShippedJobs) {
      // Filter out jobs that have been shipped (have ActualShipDate)
      moveRequests = moveRequests.filter(job => !job.ActualShipDate);
    }
    
    // Sort the jobs with clear priority: actionable items first, then completed/shipped items
    return moveRequests.sort((a, b) => {
      // Helper function to determine job category (for sorting)
      const getJobCategory = (job) => {
        // Priority 1: Active move requests that can still be acted upon
        if (!job.MoveRequestDateMovedUpOut && !job.ActualShipDate) {
          return 1;
        }
        // Priority 2: Completed moves
        if (job.MoveRequestDateMovedUpOut) {
          return 2;
        }
        // Priority 3: Shipped jobs (lowest priority)
        if (job.ActualShipDate) {
          return 3;
        }
        // Fallback
        return 4;
      };
      
      const categoryA = getJobCategory(a);
      const categoryB = getJobCategory(b);
      
      // First sort by category
      if (categoryA !== categoryB) {
        return categoryA - categoryB;
      }
      
      // Within the same category, sort by JobNumber
      return (a.JobNumber || '').localeCompare(b.JobNumber || '');
    });
  }, [jobScheduleData, filters]);

  const sortedMoveRequestJobs = useMemo(
    () => sortTableRows(moveRequestJobs, TABLE_COLUMNS, sortConfig),
    [moveRequestJobs, sortConfig]
  );

  const handleSort = (key) => {
    setSortConfig((currentSort) => getNextSortConfig(currentSort, key));
  };

  // Helper function to determine row class based on move request status
  const getRowClass = (job) => {
    // Check if job has been shipped (highest priority for visual distinction)
    if (job.ActualShipDate) {
      // If it's also an unfulfilled move request, show that
      if (isUnfulfilledMoveRequest(job)) {
        return 'unfulfilled-move-request-row'; // Light red for unfulfilled move requests
      }
      // Otherwise show as shipped
      return 'shipped-job-row'; // Light yellow for shipped jobs
    }
    
    // Check if job has been officially moved
    if (job.MoveRequestDateMovedUpOut) {
      return 'moved-job-row'; // Light blue for moved jobs
    }
    
    return ''; // Default (no special highlighting)
  };

  const handleFilterChange = (filterName) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: !prev[filterName]
    }));
  };

  const handleOpenModal = (job) => {
    if (!hasEditAccess) return; // Check if user has edit permissions
    
    setSelectedJob(job);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedJob(null);
  };

  // Handler for edit job modal
  const handleEditJob = (job) => {
    if (!hasEditAccess) return; // Check if user has edit permissions
    
    setSelectedJob(job);
    setShowEditModal(true);
  };

  // Close handler for edit job modal
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedJob(null);
  };

  // Handler for when changes are saved in edit modal
  const handleSaveChanges = () => {
    // We don't need to do anything extra here as the EditJobModal 
    // component already updates the Redux store
  };

  const compactFields = [
    { key: "ProjectName", label: "Project Name", className: "compact-inline-project-column" },
    { key: "ModelNumber", label: "Model Number", className: "compact-inline-model-column" },
    { key: "PackDue", label: "Pack Due", render: (job) => formatDateToMMDD(job.PackDue) },
    { key: "TransportType", label: "Transport Type" },
    { key: "OriginalScheduledShipDate", label: "Original Ship Date", render: (job) => formatDateToMMDD(job.OriginalScheduledShipDate) },
    { key: "CurrentPlanShipDate", label: "Current Ship Date", render: (job) => formatDateToMMDD(job.CurrentPlanShipDate) },
    { key: "SalesRep", label: "Sales Rep" },
    { key: "JobLocation", label: "Job Location" },
    { key: "MoveRequestReasonForMove", label: "Reason for Move", className: "notes-column" },
    { key: "MoveStatus", label: "Move Status", render: getMoveStatus },
  ];

  return (
    <div className="move-requests-container">
      <div className="controls-section mb-3">
        <Button
          variant="outline-primary"
          className="schedule-filter-toggle"
          onClick={() => setShowCompactFilters((isOpen) => !isOpen)}
          aria-expanded={showCompactFilters}
          aria-controls="move-request-filters"
        >
          <i className="bi bi-funnel me-2" aria-hidden="true"></i>
          Filters
          <i className={`bi ${showCompactFilters ? 'bi-chevron-up' : 'bi-chevron-down'} ms-auto`} aria-hidden="true"></i>
        </Button>
        <div
          id="move-request-filters"
          className={`schedule-filter-panel ${showCompactFilters ? 'is-open' : ''}`}
        >
          <div className="filters d-flex justify-content-end align-items-center mb-3">
            <Form.Check 
              type="switch"
              id="showMovedJobsSwitch"
              label="Show Moved Jobs"
              checked={filters.showMovedJobs}
              onChange={() => handleFilterChange('showMovedJobs')}
              className="me-3"
            />
            <Form.Check 
              type="switch"
              id="showUnfulfilledMoveRequestsSwitch"
              label="Show Unfulfilled Move Requests"
              checked={filters.showUnfulfilledMoveRequests}
              onChange={() => handleFilterChange('showUnfulfilledMoveRequests')}
              className="me-3"
            />
            <Form.Check 
              type="switch"
              id="showShippedJobsSwitch"
              label="Show Shipped Jobs"
              checked={filters.showShippedJobs}
              onChange={() => handleFilterChange('showShippedJobs')}
            />
          </div>
        </div>
      </div>

      <div className="schedule-simple-desktop-table">
        <Table striped bordered hover responsive className="schedule-responsive-table">
        <SortableTableHeader
          columns={TABLE_COLUMNS}
          sortConfig={sortConfig}
          onSort={handleSort}
        />
        <tbody>
          {sortedMoveRequestJobs.length > 0 ? (
            sortedMoveRequestJobs.map(job => (
              <tr key={job.ID} className={`schedule-collapsible-row ${getRowClass(job)}`}>
                <td data-label="Job Number" className="schedule-card-primary">{job.JobNumber}</td>
                <td data-label="Model Number" className="schedule-card-primary">{job.ModelNumber}</td>
                <td data-label="Project Name" className="schedule-card-primary">{job.ProjectName}</td>
                <td data-label="Pack Due" className="schedule-card-secondary">{formatDateToMMDD(job.PackDue)}</td>
                <td data-label="Transport Type" className="schedule-card-secondary">{job.TransportType}</td>
                <td data-label="Original Ship Date" className="schedule-card-secondary">{formatDateToMMDD(job.OriginalScheduledShipDate)}</td>
                <td data-label="Current Ship Date" className="schedule-card-secondary">{formatDateToMMDD(job.CurrentPlanShipDate)}</td>
                <td data-label="Sales Rep" className="schedule-card-secondary">{job.SalesRep}</td>
                <td data-label="Job Location" className="schedule-card-secondary">{job.JobLocation}</td>
                <td data-label="Requested Date" className="schedule-card-secondary">{job.MoveRequestRequestedDate}</td>
                <td data-label="Reason for Move" className="schedule-card-secondary">{job.MoveRequestReasonForMove}</td>
                <td data-label="Move Direction" className="text-center schedule-card-primary">
                  {getMoveRequestIcon(job) && (
                    <i 
                      className={getMoveRequestIcon(job)} 
                      style={{ 
                        color: job.MoveRequestDateMovedUpOut ? 'green' : 'blue',
                        fontSize: '1.2em'
                      }}
                      title={getMoveRequestTooltipText(job)}
                    ></i>
                  )}
                </td>
                <td data-label="Actions">
                  <Button 
                    variant="primary" 
                    size="sm"
                    className="schedule-card-action"
                    onClick={() => handleOpenModal(job)}
                    disabled={!hasEditAccess || !!job.ActualShipDate}
                  >
                    Move
                  </Button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="13" className="text-center schedule-empty-row">
                No move requests found
              </td>
            </tr>
          )}
        </tbody>
        </Table>
      </div>

      <div className="schedule-simple-compact-list">
        <CompactScheduleSection
          title={`Move Requests (${sortedMoveRequestJobs.length})`}
          jobs={sortedMoveRequestJobs}
          dateLabel="Requested Date"
          getDateValue={(job) => formatDateToMMDD(job.MoveRequestRequestedDate)}
          fields={compactFields}
        />
      </div>

      {/* MoveRequestModal */}
      {showModal && selectedJob && (
        <MoveRequestModal
          show={showModal}
          job={selectedJob}
          onHide={handleCloseModal}
          onEditJob={handleEditJob}
          canEdit={hasEditAccess}
        />
      )}

      {/* EditJobModal */}
      <Modal className="job-schedule-modal" show={showEditModal} onHide={handleCloseEditModal} size="xl">
        {showEditModal && selectedJob && (
          <EditJobModal
            selectedJob={selectedJob}
            setSelectedJob={setSelectedJob}
            handleCloseModal={handleCloseEditModal}
            handleSaveChanges={handleSaveChanges}
            canEdit={hasEditAccess}
          />
        )}
      </Modal>

      <style>
        {`
          .move-requests-container {
            padding: 1rem;
          }
          
          .controls-section {
            margin-bottom: 1rem;
          }
          
          .table th, .table td {
            vertical-align: middle;
          }
          
          /* Row highlight classes */
          .moved-job-row {
            background-color: #e6f7ff !important; /* Light blue */
          }
          
          .unfulfilled-move-request-row {
            background-color: #ffe6e6 !important; /* Light red */
          }
          
          .past-move-date-row {
            background-color: #f5f5f5 !important; /* Light gray */
            opacity: 0.7;
          }
          
          .shipped-job-row {
            background-color: #fff7e6 !important; /* Light yellow */
          }
          
          /* Override the striped table style for highlighted rows */
          .table-striped tbody tr.moved-job-row:nth-of-type(odd),
          .table-striped tbody tr.moved-job-row:nth-of-type(even) {
            background-color: #e6f7ff !important;
          }
          
          .table-striped tbody tr.unfulfilled-move-request-row:nth-of-type(odd),
          .table-striped tbody tr.unfulfilled-move-request-row:nth-of-type(even) {
            background-color: #ffe6e6 !important;
          }
          
          .table-striped tbody tr.past-move-date-row:nth-of-type(odd),
          .table-striped tbody tr.past-move-date-row:nth-of-type(even) {
            background-color: #f5f5f5 !important;
            opacity: 0.7;
          }
          
          .table-striped tbody tr.shipped-job-row:nth-of-type(odd),
          .table-striped tbody tr.shipped-job-row:nth-of-type(even) {
            background-color: #fff7e6 !important;
          }
        `}
      </style>
    </div>
  );
};

export default MoveRequests;
